
/**
 * An example address:
 *
 * postalCountry: US
 * streetAddress: 1098 Alta Ave
 * administrativeArea: CA
 * locality: Mountain View
 * dependentLocality:
 * postalCode: 94043
 * sortingCode:
 * organization: Google
 * recipient: Chen-Kang Yang
 * language code: en
 *
 */

export class Address {

  public latitude: number;

  public longitude: number;

  // CLDR (Common Locale Data Repository) country code.
  public postalCountry: string;

  // The most specific part of any address. They may be left empty if more detailed fields are used
  // instead, or they may be used in addition to these if the more detailed fields do not fulfill
  // requirements, or they may be used instead of more detailed fields to represent the street-level
  // part.
  public addressLines: string[];

  // Top-level administrative subdivision of this country.
  public administrativeArea: string;

  // Generally refers to the city/town portion of an address.
  public locality: string;

  // Dependent locality or sublocality. Used for neighborhoods or suburbs.
  public dependentLocality: string;

  // Values are frequently alphanumeric.
  public postalCode: string;

  // This corresponds to the SortingCode sub-element of the xAL PostalServiceElements element.
  // Use is very country-specific.
  public sortingCode: string;

  // The firm or organization. This goes at a finer granularity than address lines in the address.
  public organization: string;

  // BCP-47 language code for the address. Can be set to null.
  public languageCode: string;

  // The recipient. This goes at a finer granularity than address lines in the address.
  public recipient: string;

  constructor(latitude: number, longitude: number, postalCountry: string, addressLines: string[], administrativeArea: string, locality: string, dependentLocality: string, postalCode: string, sortingCode: string, organization: string, languageCode: string, recipient: string) {
    this.latitude = latitude,
    this.longitude = longitude,
    this.postalCountry = postalCountry,
    this.addressLines = addressLines;
    this.administrativeArea = administrativeArea;
    this.locality = locality;
    this.dependentLocality = dependentLocality;
    this.postalCode = postalCode;
    this.sortingCode = sortingCode;
    this.organization = organization;
    this.languageCode = languageCode;
    this.recipient = recipient;
  }

  public getFullAddress(): string {
    return this.addressLines[0] + ', ' + this.locality + ', ' + this.administrativeArea + ' ' + this.postalCode;
  }

  public static mapFromFirestore(address: any): Address {
    return new Address(
      address.latitude,
      address.longitude,
      address.postalCountry,
      address.addressLines,
      address.administrativeArea,
      address.locality,
      address.dependentLocality,
      address.postalCode,
      address.sortingCode,
      address.organization,
      address.languageCode,
      address.recipient
    );
  }

  public mapToFirestore(): any {
    return {
      latitude: this.latitude,
      longitude: this.longitude,
      postalCountry: this.postalCountry,
      addressLines: this.addressLines,
      administrativeArea: this.administrativeArea,
      locality: this.locality,
      dependentLocality: this.dependentLocality,
      postalCode: this.postalCode,
      sortingCode: this.sortingCode,
      organization: this.organization,
      languageCode: this.languageCode,
      recipient: this.recipient
    };
  }

  public static createAddressFromPlace(place: google.maps.places.PlaceResult | google.maps.GeocoderResult): Address {
    var latitude: number;
    var longitude: number;
    var postalCountry: string = '';
    var addressLines: string[] = [];
    var administrativeArea: string = '';
    var locality: string = '';
    var dependentLocality: string = '';
    var postalCode: string = '';
    var sortingCode: string = '';
    var organization: string = '';
    var languageCode: string = '';
    var recipient: string = '';
    var street: string = '';
    var route: string = '';

    if (place.geometry) {
      latitude = place.geometry.location.lat();
      longitude = place.geometry.location.lng();
    }

    place.address_components.forEach((comp: google.maps.GeocoderAddressComponent) => {
      let type = comp.types[0];
      switch (type) {
        case 'country':
          postalCountry = comp.short_name;
          break;
        case 'administrative_area_level_1':
          administrativeArea = comp.short_name;
          break;
        case 'locality':
          locality = comp.short_name;
          break;
        case 'administrative_area_level_2':
          dependentLocality = comp.short_name;
          break;
        case 'postal_code':
          postalCode = comp.short_name;
          break;
        case 'street_number':
          street = comp.short_name;
          break;
        case 'route':
          route = comp.short_name;
          break;
      }
    });

    addressLines.push((street + ' ' + route));

    return new Address(
      latitude,
      longitude,
      postalCountry,
      addressLines,
      administrativeArea,
      locality,
      dependentLocality,
      postalCode,
      sortingCode,
      organization,
      languageCode,
      recipient
    );
  }
}
